import React, {
  FC,
  ReactNode,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Dropdown } from '@shared/components';
import PlusCircleIcon from '@icons/PlusCircleIcon/PlusCircleIcon';
import ChatUploadIcon from '@icons/ChatUploadIcon/ChatUploadIcon';
import AddDatabaseFilesModal from '@features/AddDatabaseFilesModal/AddDatabaseFilesModal';
import { FileTag } from '@components/UploadForm/ui/FileTag/FileTag';
import { CaseFile } from '@api/document/model/types';
import { DocumentGeneratorInstanceVersionFile } from '@app/types/document-instance';
import styles from './TextareaField.module.scss';

interface IInputFieldProps {
  placeholder: string;
  name: string;
  error?: boolean;
  className?: string;
  image?: ReactNode;
  tabIndex?: number;
  ref?: any;
  onChange?: any;
  inputWrapperClassName?: string;
  bordered: boolean;
  onResize?: (height: any) => void;
  onKeyUp?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  value?: string;
  readOnly?: boolean;
  maxPromptHeight?: string;
  isUploadButton?: boolean;
  files?:
    | File[]
    | CaseFile[]
    | SectionFile[]
    | DocumentGeneratorInstanceVersionFile[];
  onFileAction?: (type: 'upload' | 'remove', data?: any) => void;
  onFileUpload?: (file: File[]) => void;
}

/**
 * @param props - The props for the component.
 * @param name - name for the input.
 * @param placeholder - placeholder for the input.
 * @param tabIndex - tab index for the input.
 * @param className - custom class name for the input.
 * @param image - An image or icon in input.
 * @param inputWrapperClassName - custom class name for the input wrapper.
 * @param ref - ref for the input.
 * @param error - Indicates error in input.
 * @param bordered - Indicates whether input should have a border
 * @param onResize - Function which is called after resizing textarea
 * @param onChange - Function which is called after changing textarea value
 * @returns A React component that displays a textarea field.
 */

const TextareaField: FC<IInputFieldProps> = React.forwardRef((props, ref) => {
  const {
    maxPromptHeight,
    className,
    image,
    error,
    bordered,
    inputWrapperClassName,
    onResize,
    onKeyUp,
    value,
    readOnly,
    isUploadButton,
    files,
    onFileAction,
    onFileUpload,
    ...rest
  } = props;
  const [_, setHeight] = useState<number>(0);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  useImperativeHandle(ref, () => textAreaRef.current);

  const onChange = (e: any) => {
    if (textAreaRef.current) {
      setHeight(textAreaRef.current.scrollHeight);
      textAreaRef.current.style.height = 'auto';
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
      if (onResize && textAreaRef.current.scrollHeight < 200) {
        onResize(textAreaRef.current.scrollHeight);
      }
    }
    rest.onChange(e);
  };

  const plusDropdownContent = (
    <div className={styles.plusDropdownBox}>
      <div
        onClick={() => setUploadModalOpen(true)}
        className={styles.plusDropdownItem}
      >
        <ChatUploadIcon stroke="#98A2B3" />
        <p>Upload from your computer</p>
      </div>
    </div>
  );
  return (
    <>
      <div
        className={[
          styles.inputWrapper,
          inputWrapperClassName,
          bordered && styles.bordered,
          error && styles.error,
        ].join(' ')}
      >
        {image}
        <textarea
          value={value}
          ref={textAreaRef}
          className={[className].join(' ')}
          style={{
            maxHeight: maxPromptHeight ?? '200px',
            overflowY: 'auto',
            resize: 'vertical',
          }}
          onKeyUp={onKeyUp}
          readOnly={readOnly}
          {...rest}
          onChange={onChange}
        />
        {isUploadButton && (
          <div className={styles.iconsWrapper}>
            <Dropdown
              closeOnClick
              content={plusDropdownContent}
              positionDiff={{ top: -22, left: 200 }}
            >
              <PlusCircleIcon stroke="#2479BF" />
            </Dropdown>
          </div>
        )}
        <AddDatabaseFilesModal
          showModal={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          onFileUpload={onFileUpload}
          onConfirm={() => setUploadModalOpen(false)}
          fetchDataBase={() => {}}
        />
      </div>
      <div>
        {files ? (
          <div className={styles.files}>
            {files.map((file, index) => (
              <FileTag
                key={index}
                file={file}
                className={className}
                removeFile={(data) => {
                  if (onFileAction) {
                    onFileAction('remove', data);
                  }
                }}
              />
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
});

export default TextareaField;
